import {gql, useQuery} from "@apollo/client";
import {useState} from "react";
import {Switch, useParams, useRouteMatch} from "react-router";
import {ClientManagementLayout} from "../layouts/ClientManagementLayout";
import {
    CLIENT_PROFILE_AUTHORIZATIONS_PATH,
    CLIENT_PROFILE_CARE_TEAM_MEMBERS_PATH,
    CLIENT_PROFILE_CLIENT_REPORTS_PATH,
    CLIENT_PROFILE_CONTACTS_PATH,
    CLIENT_PROFILE_DOCUMENT_FILES_PATH,
    CLIENT_PROFILE_SUBMITTED_REQUEST_PATH,
    CLIENT_PROFILE_PROGRESS_PORTRAITS_PATH,
    CLIENT_PROFILE_REFERRAL_OFFER_PATH,
    CLIENT_PROFILE_UTILIZATION_PATH,
    ClientInfo,
    TeamContact
} from "./domain";
import {LoadingSpinner} from "../../common";
import {ClientProfileTopBar} from "./ClientProfileTopBar";
import {ClientProfileLayout} from "./ClientProfileLayout";
import {ClientProfileNavigationTab} from "./ClientProfileNavigationTab";
import {PortalSecureRoute} from "../../security";
import {ClientProfileContactDetail} from "./ClientProfileContactDetail";
import {ClientProfileAuthorizationPage} from "./ClientProfileAuthorizationPage";
import {ClientProfileClientReportPage} from "./ClientProfileClientReportPage";
import {toRelayId} from "../../graphqlsupport";
import {ClientProfileDocumentFileListPage} from "./ClientProfileDocumentFileListPage";
import {ClientProfilePractitionerCareTeamListPage} from "./care-team/ClientProfilePractitionerCareTeamListPage";
import { AzurePortalSecuredRoute } from "security/component/azure/AzurePortalSecuredRoute";
import {ClientProfileSubmittedRequestListPage} from "./ClientProfileSubmittedRequestPage";
import {ClientProfileProgressPortraitPage} from "./progress-portrait/ClientProfileProgressPortraitPage";
import {ClientProfileReferralOfferPage} from "./referral-offer/ClientProfileReferralOfferPage";
import {ClientProfileUtilizationPage} from "./ClientProfileUtilizationPage";
import { ProviderPortalFeatureFlag, useFeatureFlag } from "featureflag";

export const CLIENT_DETAIL_QUERY = gql`
query clientProfileQuery($clientProfileGuid: ID!) {
    teamContactsForClientProfileQuery(id: $clientProfileGuid) {
       firstName
       lastName
       email
       phone
       mobile
       memberRole
    }
    clientProfileQuery(
        id: $clientProfileGuid
    ) {
        guid
        name
        fullName
        birthDate
        genderInfo
        email
        medicalRecordNumber
        age
        city
        status
        preferredLanguages
        bhpnClientId
        referralAcceptedDate
        anticipatedDischargeDate
        clientRelatedContacts {
            primaryContact
            relationshipToClient
            name
            email
            homePhone
            mobilePhone
            workPhone
            street
            city
            stateCode
            zipCode
        }
        providerAnticipatedDischargeDate {
            sfId
            anticipatedDischargeDate
            providerConfirmedDischargeDate
        }
        dualProvider
        dualProviderOtherServices {
            clientId
            providerId
            providerName
            serviceType
        }
        latestCareCoordinationRequest {
            guid
            status
            submittedDate
        }
    }
}
`

type ClientDetailResult = {
    clientProfileQuery: ClientInfo;
    teamContactsForClientProfileQuery: TeamContact[];
}

export function ClientProfilePage({isAzure}: {isAzure: boolean}) {
    const {url: baseUrl} = useRouteMatch();
    const {clientGuid} = useParams<{ clientGuid: string; }>();
    const hideUtilizationTab = useFeatureFlag(ProviderPortalFeatureFlag.BHPNPP_3236_IS_UTILIZED_HOURS_DISABLED);
    const hideProgressPortrait = useFeatureFlag(ProviderPortalFeatureFlag.BHPNPP_3219_PROGRESS_PORTRAIT_DISABLED);

    const {loading, data, error, refetch} = useQuery<ClientDetailResult, {}>(CLIENT_DETAIL_QUERY, {
        variables: {
            clientProfileGuid: toRelayId(clientGuid, 'ClientProfile')
        }
    })

    const [detailsExpanded, setDetailsExpanded] = useState(true);

    if (loading) {
        return (
            <LoadingSpinner spacing={20} centerScreen/>
        );
    }

    if (error) {
        console.log(error);
        return (
            <div>An error happened</div>
        );
    }

    const clientProfile = data?.clientProfileQuery;
    const teamContacts = data?.teamContactsForClientProfileQuery;

    return (
        <ClientManagementLayout hasFooter={false}>
            <div className='m-0 p-0'>
                <ClientProfileLayout
                    detailsExpanded={detailsExpanded}
                    topBar={
                        <ClientProfileTopBar
                            clientInfo={clientProfile}
                            teamContacts={teamContacts}
                            expanded={detailsExpanded}
                            onToggle={() => setDetailsExpanded((prev) => !prev)}
                            clientGuid={clientGuid}
                            onRefresh={() => refetch()}
                        />
                    }
                    topRow={<ClientProfileNavigationTab baseUrl={baseUrl} hideUtilizationTab={hideUtilizationTab} hideProgressPortraitTab={hideProgressPortrait}/>}
                    bottomRow={
                        <Routes isAzure={isAzure} baseUrl={baseUrl} clientProfile={clientProfile!!} clientGuid={clientGuid} hideUtilizationTab={hideUtilizationTab} hideProgressPortrait={hideProgressPortrait}/>
                    }/>
            </div>
        </ClientManagementLayout>
    );

    }

    export const Routes = ({isAzure, baseUrl, clientProfile, clientGuid, hideUtilizationTab, hideProgressPortrait}: {isAzure: boolean, baseUrl: string, clientProfile: ClientInfo, clientGuid: string, hideUtilizationTab: boolean, hideProgressPortrait: boolean}) => {
        return (
            <Switch>
                {
                    !isAzure ? (
                        <>
                                             <PortalSecureRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_CONTACTS_PATH}`}
                                                 children={<ClientProfileContactDetail clientProfile={clientProfile}/>}/>
                                             <PortalSecureRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_AUTHORIZATIONS_PATH}`}
                                                 children={<ClientProfileAuthorizationPage clientInfo={clientProfile!!} clientGuid={clientGuid}/>}/>
                                             <PortalSecureRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_CLIENT_REPORTS_PATH}`}
                                                 children={<ClientProfileClientReportPage clientGuid={clientGuid}/>}/>
                                             <PortalSecureRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_DOCUMENT_FILES_PATH}`}
                                                 children={<ClientProfileDocumentFileListPage clientGuid={clientGuid}/>}/>
                                             <PortalSecureRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_SUBMITTED_REQUEST_PATH}`}
                                                 children={<ClientProfileSubmittedRequestListPage clientGuid={clientGuid} />}/>
                                             {!hideProgressPortrait && (
                                                 <PortalSecureRoute
                                                     path={`${baseUrl}/${CLIENT_PROFILE_PROGRESS_PORTRAITS_PATH}`}
                                                     children={<ClientProfileProgressPortraitPage clientGuid={clientGuid} />}/>
                                             )}
                                             <PortalSecureRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_CARE_TEAM_MEMBERS_PATH}`}
                                                 children={<ClientProfilePractitionerCareTeamListPage
                                                     clientGuid={clientGuid}/>}/>
                                             <PortalSecureRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_REFERRAL_OFFER_PATH}`}
                                                 children={<ClientProfileReferralOfferPage clientGuid={clientGuid} />}/>
                                             {!hideUtilizationTab && (
                                                 <PortalSecureRoute
                                                     path={`${baseUrl}/${CLIENT_PROFILE_UTILIZATION_PATH}`}
                                                     children={<ClientProfileUtilizationPage clientGuid={clientGuid} clientInfo={clientProfile}/>}/>
                                             )}
                        </>
                    ) : (
                        <>
                                             <AzurePortalSecuredRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_CONTACTS_PATH}`}
                                                 children={<ClientProfileContactDetail clientProfile={clientProfile}/>}/>
                                             <AzurePortalSecuredRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_AUTHORIZATIONS_PATH}`}
                                                 children={<ClientProfileAuthorizationPage clientInfo={clientProfile!!} clientGuid={clientGuid}/>}/>
                                             <AzurePortalSecuredRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_CLIENT_REPORTS_PATH}`}
                                                 children={<ClientProfileClientReportPage clientGuid={clientGuid}/>}/>
                                             <AzurePortalSecuredRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_DOCUMENT_FILES_PATH}`}
                                                 children={<ClientProfileDocumentFileListPage clientGuid={clientGuid}/>}/>
                                             <AzurePortalSecuredRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_SUBMITTED_REQUEST_PATH}`}
                                                 children={<ClientProfileSubmittedRequestListPage clientGuid={clientGuid}/>}/>
                                             {!hideProgressPortrait && (
                                                 <AzurePortalSecuredRoute
                                                     path={`${baseUrl}/${CLIENT_PROFILE_PROGRESS_PORTRAITS_PATH}`}
                                                     children={<ClientProfileProgressPortraitPage clientGuid={clientGuid}/>}/>
                                             )}
                                             <AzurePortalSecuredRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_CARE_TEAM_MEMBERS_PATH}`}
                                                 children={<ClientProfilePractitionerCareTeamListPage
                                                     clientGuid={clientGuid}/>}/>
                                             <AzurePortalSecuredRoute
                                                 path={`${baseUrl}/${CLIENT_PROFILE_REFERRAL_OFFER_PATH}`}
                                                 children={<ClientProfileReferralOfferPage clientGuid={clientGuid} />}/>
                                             {!hideUtilizationTab && (
                                                 <AzurePortalSecuredRoute
                                                     path={`${baseUrl}/${CLIENT_PROFILE_UTILIZATION_PATH}`}
                                                     children={<ClientProfileUtilizationPage clientGuid={clientGuid} clientInfo={clientProfile}/>}/>
                                             )}
                        </>
                    )
                }
            </Switch>
        );
    }
